import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { useSelector } from "react-redux";
import {
	ExpansionPanel,
	ExpansionPanelDetails,
	ExpansionPanelSummary,
	Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { TimesheetList } from "features/timesheet/components/timesheet-list";
import { safetyWiseApi } from "utils/safetyWiseApi";

const useStyles = makeStyles((theme) => ({
	root: {
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.white,
		overflow: "hidden",
		boxShadow: "none",
		"&:before": {
			display: "none",
		},
	},
	summary: {
		padding: theme.spacing(0, 1.5, 0, 2),
		minHeight: 52,
		"&.Mui-expanded": {
			minHeight: 52,
		},
		"& .MuiExpansionPanelSummary-content": {
			margin: theme.spacing(1.25, 0),
			alignItems: "center",
		},
	},
	summaryTitle: {
		...theme.typography.body1,
		fontWeight: 600,
		fontSize: "1rem",
		color: theme.palette.text.primary,
	},
	countMuted: {
		fontWeight: 500,
		color: theme.palette.text.secondary,
		marginLeft: theme.spacing(0.5),
	},
	expandIcon: {
		color: theme.palette.text.secondary,
	},
	details: {
		padding: 0,
		display: "block",
		overflow: "hidden",
	},
}));

const EquipmentTimesheetsPanel = ({
	equipmentId,
	expanded,
	onExpandedChange,
}) => {
	const classes = useStyles();
	const [timesheetCount, setTimesheetCount] = useState(null);
	const currentOrganisationGUID = useSelector(
		({ organisations }) => organisations.currentOrganisation
	);

	useEffect(() => {
		let cancelled = false;
		if (!currentOrganisationGUID || !equipmentId) {
			setTimesheetCount(null);
		} else {
			safetyWiseApi
				.get(
					`${currentOrganisationGUID}/organisation-equipment/${equipmentId}/timesheet-count`
				)
				.then((res) => {
					if (cancelled) {
						return;
					}
					const raw = res.data;
					if (raw === undefined || raw === null || raw === "") {
						setTimesheetCount(null);
					} else {
						setTimesheetCount(raw);
					}
				})
				.catch(() => {
					if (!cancelled) {
						setTimesheetCount(null);
					}
				});
		}
		return () => {
			cancelled = true;
		};
	}, [currentOrganisationGUID, equipmentId]);

	if (!equipmentId) {
		return null;
	}

	const countLabel =
		timesheetCount !== null && timesheetCount !== undefined
			? ` (${timesheetCount})`
			: "";

	return (
		<ExpansionPanel
			className={classes.root}
			elevation={0}
			expanded={expanded}
			onChange={onExpandedChange}
			data-cy="equipment-timesheets-panel"
		>
			<ExpansionPanelSummary
				expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
				className={classes.summary}
				aria-controls="equipment-timesheets-panel-content"
				id="equipment-timesheets-panel-header"
			>
				<Typography component="h2" className={classes.summaryTitle}>
					Timesheets
					{countLabel !== "" && (
						<span className={classes.countMuted}>{countLabel}</span>
					)}
				</Typography>
			</ExpansionPanelSummary>
			<ExpansionPanelDetails className={classes.details}>
				{expanded && (
					<TimesheetList
						variant="embedded"
						embeddedEquipmentId={equipmentId}
					/>
				)}
			</ExpansionPanelDetails>
		</ExpansionPanel>
	);
};

EquipmentTimesheetsPanel.propTypes = {
	equipmentId: PropTypes.string,
	expanded: PropTypes.bool.isRequired,
	onExpandedChange: PropTypes.func.isRequired,
};

export default EquipmentTimesheetsPanel;
